import mainServerClient from "../http/mainServerClient";
import { post } from "../http/platformServer";
import OrderDTO from "@/dto/order/OrderDTO";
import OrderMergeSplitStatus from "@/enum/OrderMergeSplitStatus";
import { PagedParams, PagedResult } from "./types";
import EwaybillDTO from "@/dto/order/EwaybillDTO";
import { EwaybillCopiesPreset } from "@/components/EwaybillCopiesSelector";

export enum AutoPrintTaskType {
    /**自动打印运单号任务 */
    WAYBILL = "WAYBILL",
    /**自动打印发货单任务 */
    PACKING_LIST = "PACKING_LIST",
}

export interface AutoPrintTaskParams {
    /**
     * 是否自动发货，可选，默认 false。
     */
    autoShipAfterPrint?: boolean;
    /**
     * 打印份数，必选，默认 1 份。
     */
    copies?: EwaybillCopiesPreset;
    /**
     * 订单付款后多久开启自动打印任务，必选，默认为空。
     */
    postPayInterval?: AutoPrintPostPayInterval;
    /**
     * 订单来源省份列表，可选，默认为空，支持多选。
     */
    provinces?: string[];
    /**
     * 选中的面单模板信息列表，必选，默认为空，支持多选。
     */
    selectedWaybillTemplates?: AutoPrintSelectedWaybillTemplateInfo[];
    /**
     * 发货地址，必选，默认发货人地址列表第一个地址即可。
     */
    shippingAddress?: AutoPrintOpenTaskShippingAddressDto;
}

export enum AutoPrintTaskMode {
    NORMAL = "NORMAL", // 自动打印(无达人筛选)
    MULTI_AFFILIATE = "MULTI_AFFILIATE", // 多达人自动打印 (按达人筛选)
}

export interface StartAutoPrintTaskParams {
    /**
     * 店铺 ID 列表，必选，默认为空，多选，只能选择平台店铺存。
     */
    shopIds?: number[];

    /**
     * @see {@link AutoPrintTaskParams}
     */
    params: string;

    type: AutoPrintTaskType;

    affiliateMarketerOriginalId?: string;

    taskMode: AutoPrintTaskMode;
}

/**
 * 订单付款后多久开启自动打印任务，必选，默认为空。
 * <p>默认值：{@link OrderPostPayInterval#IMMEDIATE}
 */
export enum AutoPrintPostPayInterval {
    /**
     * 立即
     */
    IMMEDIATE = "IMMEDIATE",
    /**
     * 5分钟
     */
    FIVE_MINUTES = "FIVE_MINUTES",
    /**
     * 10分钟
     */
    TEN_MINUTES = "TEN_MINUTES",
    /**
     * 15分钟
     */
    FIFTEEN_MINUTES = "FIFTEEN_MINUTES",
    /**
     * 30分钟
     */
    THIRTY_MINUTES = "THIRTY_MINUTES",
    /**
     * 60分钟
     */
    ONE_HOUR = "ONE_HOUR",
    /**
     * 120分钟
     */
    TWO_HOURS = "TWO_HOURS",

    THREE_HOURS = "THREE_HOURS",
}

// export const PostPayIntervalMap = {
//     [PostPayInterval.IMMEDIATE]: 0,
//     [PostPayInterval.FIVE_MINUTES]: 0,
//     [PostPayInterval.TEN_MINUTES]: 0,
//     [PostPayInterval.FIFTEEN_MINUTES]: 0,
//     [PostPayInterval.THIRTY_MINUTES]: 0,
//     [PostPayInterval.ONE_HOUR]: 0,
//     [PostPayInterval.TWO_HOURS]: 0,
// };

/**
 * 选择运单模板信息。
 */
export interface AutoPrintSelectedWaybillTemplateInfo {
    /**
     * 选择模板时候的标签名称，用于展示：<ul>
     * <li>淘系快递单模板</li>
     * <li>拼多多快递单模板</li>
     * <li>1688 快递单模板</li>
     * <li>...</li>
     * </ul>
     * <p>前端自定义传入。
     */
    labelName?: string;
    /**
     * 打印机名称。
     */
    printerName?: string;
    /**
     * 快递单模板 ID，担心模板重名，用于日后排查问题。
     */
    templateId?: number;
    /**
     * 快递单模板的名称。
     */
    templateName?: string;
}

/**
 * 发货地址，必选，默认发货人地址列表第一个地址即可。
 *
 * ShippingAddressDto
 */
export interface AutoPrintOpenTaskShippingAddressDto {
    /**
     * 城市
     */
    city?: string;
    /**
     * 区县
     */
    district?: string;
    /**
     * 发货人ID
     */
    id?: number;
    /**
     * 手机
     */
    mobile?: string;
    /**
     * 省份
     */
    province?: string;
    /**
     * 名称
     */
    shipperName?: string;
    /**
     * 详细地址
     */
    street?: string;
    /**
     * 固话
     */
    telephone?: string;
}

/**
 * 自动打印任务状态。
 */
export enum AutoPrintTaskStatus {
    COMPLETED = "COMPLETED",
    EXPIRED = "EXPIRED",
    RUNNING = "RUNNING",
}

interface AutoPrintTask {
    affiliateMarketerOriginalId?: string;

    /**
     * 操作人 IP。
     */
    opIp?: string;
    /**
     * 操作人所属店铺 ID。
     */
    opShopId?: number | null;
    /**
     * 操作人 ID。
     */
    opUserId?: number | null;
    /**
     * 操作人用户名。
     */
    opUsername?: string;

    /**
     * 自动打印任务过期时间。
     */
    expirationTime?: string;
    /**
     * 自动打印任务关联的店铺 ID 列表。
     */
    shopIds?: number[];
    /**
     * 自动打印任务状态。
     */
    status?: AutoPrintTaskStatus;
    /**
     * 自动打印任务 token。
     */
    token?: string;

    type: AutoPrintTaskType;

    taskMode: AutoPrintTaskMode;
}

/**
 * 开启自动打印任务
 */
export function startAutoPrintTask(params: StartAutoPrintTaskParams) {
    return mainServerClient.post<{
        task: AutoPrintTask;
        openTaskErrorMap: Record<number, string>;
    }>("/auto_print_task/start_task", params);
}

/**
 * 结束自动打印任务
 * @param token 自动打印任务 token
 * @param isAuto 是否是系统手动关闭（超过打印订单的限制等）
 * @param remark 备注
 * @returns
 */
export function stopAutoPrintTask(token: string, isAuto: boolean, remark?: string) {
    return mainServerClient.post("/auto_print_task/stop_task", {
        token,
        isAuto,
        remark,
    });
}

/**
 * 延迟自动打印任务过期时间。
 */
export function extendAutoPrintTaskExpirationTime(token: string) {
    return mainServerClient.post<AutoPrintTask>(
        "/auto_print_task/extend_expiration_time",
        {
            token,
        },
        {
            hideModal: true,
        },
    );
}

/**
 * 查询与指定店铺相关的所有处于 OPEN 状态的任务列表。
 */
export function getRunningAutoPrintTask(shopIds: number[]) {
    return mainServerClient.post<AutoPrintTask[]>("/auto_print_task/list_running_tasks", {
        shopIds,
    });
}

/**
 * 增量获取订单
 */
export function fetchIncrementalOrders(params: FetchIncrementalOrderParams) {
    return post<FetchOrdersResult>(params.shopId, "/auto_print/order/fetch_incremental_orders", params);
}

export interface FetchIncrementalOrderParams {
    cursor?: FetchOrdersCursor;
    /**
     * 每次查询尽量返回的订单数量，不传的时候，默认值为 10。
     */
    pageSize?: number;
    /**
     * 查询付款后多久的订单，必选，默认为空。
     * <p>默认值：{@link AutoPrintPostPayInterval.IMMEDIATE}
     */
    postPayInterval: AutoPrintPostPayInterval;
    /**
     * 订单来源省份列表，可选，默认为空，支持多选。
     */
    provinceCodes?: string[];
    /**
     * 待查询订单的店铺ID。
     */
    shopId: number;
    /**
     * 用于验证请求合法性的 taskToken，自动打印任务的 token。
     */
    taskToken: string;

    /**
     * 最小支付时间
     */
    minPaymentTime: string;

    affiliateMarketerOriginalId?: string;
}

/**
 * 获取订单结果。
 */
export interface FetchOrdersResult {
    /**
     * 下一次查询的游标。
     */
    nextCursor?: FetchOrdersCursor;
    /**
     * 查询到的平台订单和发货易订单映射关系列表。
     */
    orders?: OrderDTO[];
}

/**
 * 下一次查询的游标。
 */
export type FetchOrdersCursor = any;

export interface SimplePlatformOrder {
    originalId?: string;
    mergeSplitStatus?: OrderMergeSplitStatus;
}

interface GetAutoPrintSwitchLogListParams extends PagedParams {
    /**
     * 待查询的店铺 ID，不能为空。
     */
    shopId: number;

    taskType: AutoPrintTaskType;
}

/**
 * 自动打印开关日志。
 *
 * AutoPrintSwitchLog
 */
export interface AutoPrintSwitchLog {
    /**
     * 自动打印开关日志 ID。
     */
    id: number;
    /**
     * 自动打印开关日志操作人 IP。
     */
    opIp?: string;
    /**
     * 自动打印开关日志创建时间。
     */
    dateCreated?: string;
    /**
     * 自动打印开关日志操作人所属店铺 ID。
     */
    opShopId?: number;
    /**
     * 自动打印开关日志操作人 ID。
     */
    opUserId?: number;
    /**
     * 自动打印开关日志操作人。
     */
    opUsername?: string;
    /**
     * 自动打印开启时的入参，只有当{@link AutoPrintSwitchLogType.OPEN} 的时候，该字段才会有值。
     */
    params?: string;
    /**
     * 自动打印开关日志所属店铺 ID。
     */
    shopId?: number;
    /**
     * 自动打印任务 ID。
     */
    taskId?: number;
    /**
     * 自动打印开关日志类型。
     */
    type?: AutoPrintSwitchLogType;

    taskType: AutoPrintTaskType;

    remark?: string;
}

/**
 * 自动打印开关日志类型。
 */
export enum AutoPrintSwitchLogType {
    CLOSE = "CLOSE", // 手动关闭
    AUTO_CLOSE = "AUTO_CLOSE", // 自动关闭
    OPEN = "OPEN",
    TIMEOUT_CLOSE = "TIMEOUT_CLOSE",
    EXPIRED_CLOSE = "EXPIRED_CLOSE",
}

/**
 * 分页查询自动打印开关日志列表。
 */
export function getAutoPrintSwitchLogList(params: GetAutoPrintSwitchLogListParams) {
    return mainServerClient.get<PagedResult<AutoPrintSwitchLog>>("/auto_print_switch_log/list", params);
}

export enum AutoPrintOrderOpLog {
    PRINT_WAYBILL = "PRINT_WAYBILL",
    PRINT_PACKING_LIST = "PRINT_PACKING_LIST",
}

export interface GetAutoPrintOrderOpLogParams extends PagedParams {
    shopId: number;
    orderOriginalId?: string;
    mergeSplitStatus?: OrderMergeSplitStatus;
    type?: AutoPrintOrderOpLog;
}

interface AutoPrintOrderLog {
    id?: number;
    /**
     * 订单所属店铺 ID。
     */
    shopId: number;
    /**
     * 打单时的订单收件人信息。
     */
    consignee?: string;
    /**
     * 操作信息
     */
    message?: string;
    orderId?: number;
    mergeSplitStatus?: OrderMergeSplitStatus;
    orderOriginalId?: string;
    /**
     * 订单付款时间。
     */
    paymentTime?: string;
    /**
     * 订单打印时间。
     */
    printTime?: string;
    /**
     * 订单发货时间。
     */
    shippingTime?: string;

    /**
     * 打印模板名称。
     */
    templateName?: string;
    time?: string;
    type?: AutoPrintOrderLogType;
    /**
     * 操作人用户名
     */
    username?: string;

    waybillNos: string;

    originalId: string;
}

export enum AutoPrintOrderLogType {
    READY_PRINT = "READY_PRINT",
    PRINT = "PRINT",
    SHIP = "SHIP",
    PRINT_FAIL = "PRINT_FAIL",
    SHIP_FAIL = "SHIP_FAIL",
    READY_PRINT_PACKING_LIST = "READY_PRINT_PACKING_LIST",
    PRINT_PACKING_LIST = "PRINT_PACKING_LIST",
    PRINT_PACKING_LIST_FAIL = "PRINT_PACKING_LIST_FAIL",
}

/**
 * 获取自动打印操作日志
 */
export function getAutoPrintOrderOpLog(params: GetAutoPrintOrderOpLogParams) {
    return post<PagedResult<AutoPrintOrderLog>>(params.shopId, "/auto_print/order/oplog/list", params);
}

export interface SaveAutoPrintOrderLogDto
    extends Omit<AutoPrintOrderLog, "id" | "username" | "time" | "orderOriginalId" | "waybillNos"> {
    ewaybills?: Pick<EwaybillDTO, "waybillNo" | "logisticsCompany" | "parentWaybillNo">[];
}

interface BatchSaveAutoPrintOrderOpLog {
    taskToken: string;

    logs: SaveAutoPrintOrderLogDto[];
}

/**
 * 批量保存自动打印日志
 */
export function batchSaveAutoPrintOrderOpLog(params: BatchSaveAutoPrintOrderOpLog) {
    const shopId = params.logs?.[0]?.shopId;

    if (!shopId) {
        return;
    }

    return post<void>(shopId, "/auto_print/order/oplog/batch_log", params, {
        hideModal: true,
    });
}
